import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/Auth";
import { RotatingLines } from "react-loader-spinner";
import { useGlobalContext } from "../../context/stateContext";
import RoleBasedComponent from "../../components/RoleBasedComponent";
import { Building2, Package, ArrowRight, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const InventoryStock = () => {
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  const { activeSession } = useGlobalContext();
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchBranches = async () => {
    try {
      const data = await requestWithAuth(
        `/company/branches/?pageSize=100`,
        "GET"
      );
      setBranches(data?.results);
    } catch (error) {
      console.error("Error fetching users data:", error);
    }
  };

  const getBranchInventory = (e) => {
    e.preventDefault();
    if (!selectedBranch) return;
    setLoading(true);
    navigate(`/inventory/branch/${selectedBranch}/`);
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                <span>Inventory</span>
                <span>/</span>
                <span className="text-gray-900 font-medium">
                  Branch Inventory
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Package size={32} className="text-blue-600" />
                Branch Inventory
              </h1>
            </div>
          </div>
        </div>

        {/* Main Content - Centered Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            {/* Card Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="text-blue-600" size={24} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Select Branch
                  </h2>
                  <p className="text-sm text-gray-600 mt-0.5">
                    Choose a branch to view its inventory stock
                  </p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-6">
                {/* Branch Selection */}
                <div>
                  <label
                    htmlFor="branch-select"
                    className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
                  >
                    <MapPin size={16} className="text-gray-500" />
                    Branch Name
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="branch-select"
                    onChange={(e) => setSelectedBranch(e.target.value)}
                    value={selectedBranch || ""}
                    required
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white transition-all"
                  >
                    <option value="">Select a branch...</option>
                    {branches.map((branch) => (
                      <RoleBasedComponent
                        key={branch.id}
                        allowedRoles={
                          branch?.is_stores
                            ? [
                              "superadmin",
                              "admin",
                              "warehouse_manager",
                              "stores",
                            ]
                            : []
                        }
                      >
                        <option value={branch.id}>{branch.name}</option>
                      </RoleBasedComponent>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Select the branch to view detailed inventory information
                  </p>
                </div>

                {/* Info Card */}
                {selectedBranch && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Package size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-blue-900">
                          Ready to view inventory
                        </p>
                        <p className="text-xs text-blue-700 mt-1">
                          Click submit to view the complete inventory stock for
                          the selected branch
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Card Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex justify-end">
                {loading ? (
                  <button
                    type="button"
                    disabled
                    className="bg-blue-400 text-white rounded-lg px-6 py-3 flex items-center gap-2 cursor-not-allowed"
                  >
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Loading Inventory...
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={getBranchInventory}
                    disabled={!selectedBranch}
                    className={`rounded-lg px-6 py-3 font-medium transition-colors shadow-sm flex items-center gap-2 ${selectedBranch
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                  >
                    View Inventory
                    <ArrowRight size={18} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">
              What you can view:
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package size={16} className="text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Stock Levels
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Current inventory quantities
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Package size={16} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Item Details
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Product information and pricing
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Package size={16} className="text-orange-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Low Stock Alerts
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Items below reorder level
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package size={16} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Stock Value
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Total inventory valuation
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InventoryStock;
